import { 
    setMaxPages, 
    setAcceptedCount, 
    setPendingCount, 
    setBlockedCount,
    setTotalCount,
    toggleCheckedItem,
    setSelectedOrganizer,
    setEditedOrganizer,
    toggleEditModal,
} from '../../store/organizersSlice';
import ApiService from '../../components/api_services/apiService.jsx';
import ActionMenu from '../../components/components/action_menu/ActionMenu.jsx';
import { ReactComponent as StatusUp } from '../../components/icons/status-up.svg';
import { ReactComponent as Edit } from '../../components/icons/edit.svg';
import { ReactComponent as TickCircle } from '../../components/icons/tick-circle.svg';
import { ReactComponent as CloseCircle } from '../../components/icons/close-circle.svg';
import { ReactComponent as Trash } from '../../components/icons/trash.svg';
import {
    ADMIN_PROFILES_PAGE_SIZE,
    filterOrganizersForTab,
    normalizeProfilesToOrganizers,
    sortRowsLocally,
} from '../profiles/profilesAdapter.js';

const DEFAULT_ORGANIZERS_PAGE_SIZE = ADMIN_PROFILES_PAGE_SIZE;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ORGANIZER_STATUS_KEYS = {
    ACTIVE: 'AKTYWNY',
    EMAIL_NOT_CONFIRMED: 'NIEPOTWIERDZONY_EMAIL',
    NEW_PROFILE_PENDING: 'NOWY_PROFIL_DO_AKCEPTACJI',
    DATA_CHANGE_PENDING: 'ZMIANA_DANYCH_DO_AKCEPTACJI',
};

const ORGANIZER_STATUS_META = {
    [ORGANIZER_STATUS_KEYS.ACTIVE]: {
        label: 'Aktywny',
        toneClass: 'organizer-status--active',
    },
    [ORGANIZER_STATUS_KEYS.EMAIL_NOT_CONFIRMED]: {
        label: 'Niepotwierdzony email',
        toneClass: 'organizer-status--inactive',
    },
    [ORGANIZER_STATUS_KEYS.NEW_PROFILE_PENDING]: {
        label: 'Nowy profil do akceptacji',
        toneClass: 'organizer-status--pending',
    },
    [ORGANIZER_STATUS_KEYS.DATA_CHANGE_PENDING]: {
        label: 'Zmiana danych do akceptacji',
        toneClass: 'organizer-status--pending',
    },
};


const extractOrganizerProfilesEntries = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.content)) return response.content;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.rows)) return response.rows;
    if (Array.isArray(response?.accountsWrapperResponseDtoList)) return response.accountsWrapperResponseDtoList;
    if (Array.isArray(response?.organiserProfiles)) return response.organiserProfiles;
    if (Array.isArray(response?.organizerProfiles)) return response.organizerProfiles;
    return [];
};

const extractOrganizerTotalCount = (response) => {
    const candidates = [
        response?.totalCount,
        response?.totalElements,
        response?.recordsCount,
        response?.count,
    ];

    const parsed = candidates
        .map((value) => Number(value))
        .find((value) => Number.isFinite(value) && value >= 0);

    return parsed ?? null;
};

const extractOrganizerTotalPages = (response) => {
    const candidates = [
        response?.maxPage,
        response?.numberOfPages,
        response?.totalPages,
        response?.pagesCount,
    ];

    const parsed = candidates
        .map((value) => Number(value))
        .find((value) => Number.isFinite(value) && value >= 0);

    return parsed ?? null;
};

const resolveOrganizerStatus = (profile) => {
    if (typeof profile?.isActivated === 'boolean') {
        if (profile.isActivated === false) return 'pending';
        return profile?.isEmailConfirmed === true ? 'confirmed' : 'unconfirmed';
    }

    const explicitStatus = String(profile?.status || '').toLowerCase();
    if (explicitStatus === 'accepted') return 'confirmed';
    if (explicitStatus === 'pending') return 'pending';
    if (explicitStatus === 'pending_change' || explicitStatus === 'data_change') return 'confirmed';
    if (explicitStatus === 'blocked') return 'unconfirmed';

    if (profile?.isApprovedByAdmin === false || profile?.isActivated === false) {
        return 'pending';
    }
    if (profile?.isBlocked === true || profile?.hasBlocked === true) {
        return 'unconfirmed';
    }

    // DTO z /api/organiser/profiles* bez pól statusowych traktujemy jako oczekujący.
    if (
        profile?.companyName !== undefined &&
        profile?.nip !== undefined &&
        profile?.street !== undefined &&
        profile?.city !== undefined &&
        profile?.postalCode !== undefined &&
        profile?.contactEmail !== undefined &&
        profile?.contactPhone !== undefined
    ) {
        return 'pending';
    }

    return 'confirmed';
};

export const getOrganizerStatuses = (organizer = {}) => {
    const statuses = [];
    const hasExplicitActivation = typeof organizer?.isActivated === 'boolean';
    const hasPendingDataChange = organizer?.hasPendingChange === true
        || organizer?.pendingType === 'data_change'
        || ['pending_change', 'data_change'].includes(String(organizer?.status || '').toLowerCase());

    if (organizer?.isActivated === false) {
        statuses.push(ORGANIZER_STATUS_KEYS.NEW_PROFILE_PENDING);
    }

    if (organizer?.isActivated === true && organizer?.isEmailConfirmed === false) {
        statuses.push(ORGANIZER_STATUS_KEYS.EMAIL_NOT_CONFIRMED);
    }

    if (organizer?.isActivated === true && organizer?.isEmailConfirmed === true) {
        statuses.push(ORGANIZER_STATUS_KEYS.ACTIVE);
    }

    if (hasPendingDataChange) {
        statuses.push(ORGANIZER_STATUS_KEYS.DATA_CHANGE_PENDING);
    }

    if (statuses.length > 0) {
        return statuses;
    }

    const status = resolveOrganizerStatus(organizer);
    if (status === 'pending') return [ORGANIZER_STATUS_KEYS.NEW_PROFILE_PENDING];
    if (status === 'unconfirmed') return [ORGANIZER_STATUS_KEYS.EMAIL_NOT_CONFIRMED];
    if (status === 'confirmed') return [ORGANIZER_STATUS_KEYS.ACTIVE];

    if (!hasExplicitActivation && hasPendingDataChange) {
        return [ORGANIZER_STATUS_KEYS.DATA_CHANGE_PENDING];
    }

    return [ORGANIZER_STATUS_KEYS.ACTIVE];
};

const resolveAddress = (profile) => {
    if (profile?.address) return profile.address;

    const street = profile?.street || profile?.addressLine || '';
    const postalCode = profile?.postalCode || profile?.zipCode || '';
    const city = profile?.city || '';
    const line2 = [postalCode, city].filter(Boolean).join(' ').trim();

    if (street && line2) return `${street}, ${line2}`;
    if (street) return street;
    if (line2) return line2;
    return '-';
};

const normalizeOrganizerFromProfile = (profile = {}) => {
    const status = resolveOrganizerStatus(profile);
    const hasPendingDataChange = profile.hasPendingChange === true
        || profile.pendingType === 'data_change'
        || ['pending_change', 'data_change'].includes(String(profile?.status || '').toLowerCase());
    const ownerId = profile.ownerId
        ?? profile.userId
        ?? profile.user?.id
        ?? profile.userResponseFullDto?.id
        ?? profile._rawProfileEntry?.userResponseFullDto?.id
        ?? null;
    const organiserProfileId = profile.organiserProfileId
        ?? profile.organizerProfileId
        ?? profile._rawProfileEntry?.organiserProfileResponseDto?.id
        ?? profile._rawProfileEntry?.organizerProfileResponseDto?.id
        ?? null;

    return {
        id: profile.id ?? organiserProfileId ?? ownerId,
        ownerId,
        userId: profile.userId ?? ownerId,
        organiserProfileId,
        organizerProfileId: profile.organizerProfileId ?? organiserProfileId,
        registrationDate: profile.registrationDate ?? profile.createdAt ?? profile.added_on_utc ?? '',
        companyName: profile.companyName ?? profile.name ?? '-',
        nip: profile.nip ?? '-',
        address: resolveAddress(profile),
        email: profile.email ?? profile.contactEmail ?? '-',
        phone: profile.phone ?? profile.contactPhone ?? '-',
        eventsCount: profile.eventsCount ?? '-',
        status,
        organizerStatuses: getOrganizerStatuses({
            ...profile,
            status,
            hasPendingChange: hasPendingDataChange,
        }),
        pendingType: profile.pendingType || (hasPendingDataChange ? 'data_change' : status === 'pending' ? 'new_profile' : ''),
        hasPendingChange: hasPendingDataChange,
        pendingChangeRequestedAt: profile.pendingChangeRequestedAt ?? profile.pendingRequestedAt ?? null,
        pendingRequestedAt: profile.pendingRequestedAt ?? profile.pendingChangeRequestedAt ?? null,
        pendingChange: profile.pendingChange ?? null,
        pendingChangePayload: profile.pendingChangePayload ?? profile.pendingPayload ?? null,
        isBlocked: profile.isBlocked === true || profile.hasBlocked === true,
        description: profile.description || '',
        facebookUrl: profile.facebookUrl || profile.socialLink || '',
        websiteUrl: profile.websiteUrl || profile.siteLink || '',
        changedFields: profile.changedFields || {},
        isActive: profile.isActive ?? profile.isActivated ?? status !== 'pending',
        isApprovedByAdmin: profile.isApprovedByAdmin ?? (status !== 'pending'),
        _rawProfileEntry: profile,
    };
};

export const normalizeOrganizerProfile = (profile = {}) => {
    if (profile?.userResponseFullDto || profile?.organiserProfileResponseDto) {
        const [normalized] = normalizeProfilesToOrganizers([profile]);
        const organiserProfile = profile?.organiserProfileResponseDto || profile?.organizerProfileResponseDto || {};
        const user = profile?.userResponseFullDto || {};
        if (!normalized) {
            return null;
        }

        return {
            ...normalized,
            ...normalizeOrganizerFromProfile({
                ...organiserProfile,
                id: normalized.id,
                ownerId: normalized.ownerId ?? user.id,
                userId: normalized.userId ?? user.id,
                organiserProfileId: normalized.organiserProfileId ?? organiserProfile.id,
                organizerProfileId: normalized.organizerProfileId ?? organiserProfile.id,
                registrationDate: normalized.registrationDate,
            }),
            _rawProfileEntry: profile,
        };
    }

    return normalizeOrganizerFromProfile(profile);
};

export const resolveOrganizerId = (organizer) => {
    if (!organizer) return null;
    return (
        organizer.id ??
        organizer.organiserProfileId ??
        organizer.organizerProfileId ??
        organizer.userId ??
        organizer?._rawProfileEntry?.organiserProfileResponseDto?.id ??
        organizer?._rawProfileEntry?.organizerProfileResponseDto?.id ??
        organizer?._rawProfileEntry?.userResponseFullDto?.id ??
        null
    );
};

const normalizeEventsCount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const pushUuidCandidate = (target, seen, value) => {
    if (!value) return;
    const candidate = String(value).trim();
    if (!UUID_PATTERN.test(candidate) || seen.has(candidate)) return;
    seen.add(candidate);
    target.push(candidate);
};

const resolveOrganizerEventCountIdCandidates = (organizer) => {
    const candidates = [];
    const seen = new Set();

    pushUuidCandidate(candidates, seen, organizer?.ownerId);
    pushUuidCandidate(candidates, seen, organizer?.userId);
    pushUuidCandidate(candidates, seen, organizer?._rawProfileEntry?.userResponseFullDto?.id);
    pushUuidCandidate(candidates, seen, organizer?._rawProfileEntry?.userResponseDto?.id);
    pushUuidCandidate(candidates, seen, organizer?._rawProfileEntry?.ownerId);
    pushUuidCandidate(candidates, seen, organizer?._rawProfileEntry?.userId);
    pushUuidCandidate(candidates, seen, organizer?.organiserId);
    pushUuidCandidate(candidates, seen, organizer?.organizerId);
    pushUuidCandidate(candidates, seen, resolveOrganizerId(organizer));

    return candidates;
};

const enrichOrganizersWithEventsCount = async (organizers, requestOptions = {}) => {
    const countById = new Map();

    return Promise.all(organizers.map(async (organizer) => {
        const idCandidates = resolveOrganizerEventCountIdCandidates(organizer);

        for (const organiserId of idCandidates) {
            if (countById.has(organiserId)) {
                return {
                    ...organizer,
                    eventsCount: countById.get(organiserId),
                };
            }

            try {
                const count = normalizeEventsCount(await ApiService.countEventsByOrganiserId(organiserId, {
                    signal: requestOptions?.signal,
                }));
                countById.set(organiserId, count);
                return {
                    ...organizer,
                    eventsCount: count,
                };
            } catch (error) {
                if (error?.code === 'ERR_CANCELED') {
                    throw error;
                }

                console.error('Error fetching organizer events count:', organiserId, error);
            }
        }

        return {
            ...organizer,
            eventsCount: normalizeEventsCount(organizer?.eventsCount),
        };
    }));
};

export const isOrganizerPending = (organizer) => {
    if (!organizer) return false;
    return getOrganizerStatuses(organizer).includes(ORGANIZER_STATUS_KEYS.NEW_PROFILE_PENDING);
};

export const hasOrganizerPendingDecision = (organizer) => {
    if (!organizer) return false;
    const statuses = getOrganizerStatuses(organizer);
    return statuses.includes(ORGANIZER_STATUS_KEYS.NEW_PROFILE_PENDING)
        || statuses.includes(ORGANIZER_STATUS_KEYS.DATA_CHANGE_PENDING);
};

export const parsePendingOrganizerPayload = (pendingChange) => {
    const rawPayload = pendingChange?.payloadJson ?? pendingChange?.payload ?? pendingChange;

    if (!rawPayload) {
        return {};
    }

    if (typeof rawPayload === 'object') {
        return rawPayload;
    }

    try {
        const parsed = JSON.parse(rawPayload);
        return typeof parsed === 'string' ? JSON.parse(parsed || '{}') : parsed;
    } catch (error) {
        return {};
    }
};

export const normalizePendingOrganizerChange = (item = {}) => ({
    organiserId: item.organiserId ?? item.organizerId ?? item.id,
    companyName: item.companyName ?? '',
    contactEmail: item.contactEmail ?? '',
    requestedAt: item.pendingChange?.requestedAt ?? item.requestedAt ?? null,
    payload: parsePendingOrganizerPayload(item.pendingChange),
    pendingChange: item.pendingChange ?? null,
});

// Fetch organizers data
export const fetchOrganizers = async (
    page, 
    sortBy, 
    sortDirection, 
    keyword, 
    _pageTab, 
    filters,
    dispatch, 
    setRawOrganizers,
    requestOptions = {},
    pageSize = DEFAULT_ORGANIZERS_PAGE_SIZE
) => {
    const effectivePageSize = pageSize || DEFAULT_ORGANIZERS_PAGE_SIZE;
    try {
        const query = {
            pageNumber: page,
            pageSize: effectivePageSize,
            filterQuery: resolveOrganizerFilterQuery(filters?.filterQuery),
            ...(keyword ? { searchQuery: keyword } : {}),
            ...(sortBy && sortDirection ? { sortBy, sortDirection } : {}),
        };

        if (requestOptions?.signal?.aborted) {
            return [];
        }

        const response = await ApiService.getOrganiserProfilesPaginated(query, requestOptions);
        const backendEntries = extractOrganizerProfilesEntries(response);

        const normalizedOrganizers = backendEntries.map((entry) => normalizeOrganizerProfile(entry)).filter(Boolean);
        const tabFilteredOrganizers = filterOrganizersForTab(normalizedOrganizers, 'all', filters || {});
        const sortedOrganizers = sortRowsLocally(tabFilteredOrganizers, sortBy, sortDirection);

        const backendTotalCount = extractOrganizerTotalCount(response);
        const backendTotalPages = extractOrganizerTotalPages(response);
        const hasBackendPaging = backendTotalCount !== null || backendTotalPages !== null;

        const totalCount = hasBackendPaging ? (backendTotalCount ?? sortedOrganizers.length) : sortedOrganizers.length;
        const totalPages = hasBackendPaging
            ? (backendTotalPages !== null
                ? Math.max(1, backendTotalPages)
                : Math.max(1, Math.ceil(totalCount / effectivePageSize)))
            : Math.max(1, Math.ceil(totalCount / effectivePageSize));

        const rowsForPage = hasBackendPaging
            ? sortedOrganizers
            : sortedOrganizers.slice(
                (page - 1) * effectivePageSize,
                page * effectivePageSize
            );

        dispatch(setAcceptedCount(0));
        dispatch(setPendingCount(0));
        dispatch(setBlockedCount(0));
        dispatch(setTotalCount(totalCount));
        const rowsForPageWithEventsCount = await enrichOrganizersWithEventsCount(rowsForPage, requestOptions);

        dispatch(setMaxPages(totalPages));
        setRawOrganizers(rowsForPageWithEventsCount);

        return rowsForPageWithEventsCount;
    } catch (error) {
        if (error?.code === 'ERR_CANCELED') {
            return [];
        }
        console.error('Error fetching organizers from /api/organiser/profiles/paginated:', error);
        dispatch(setAcceptedCount(0));
        dispatch(setPendingCount(0));
        dispatch(setBlockedCount(0));
        dispatch(setTotalCount(0));
        dispatch(setMaxPages(Math.max(1, page)));
        setRawOrganizers([]);
        return [];
    }
};

// Map organizers to table rows
export const mapOrganizersToRows = (organizers, checkedItems, dispatch) => {
    const openOrganizerDetails = async (organizer) => {
        const organizerId = resolveOrganizerId(organizer);
        if (!organizerId) return;

        try {
            const response = await ApiService.getOrganiserProfile(organizerId);
            const profile = normalizeOrganizerProfile(response?.data ?? response);
            dispatch(setSelectedOrganizer(profile || organizer));
        } catch (error) {
            console.error('Blad pobierania szczegolow organizatora:', error);
            dispatch(setSelectedOrganizer(organizer));
        }
    };

    const openOrganizerEdit = async (organizer) => {
        const organizerId = resolveOrganizerId(organizer);
        if (!organizerId) return;

        try {
            const response = await ApiService.getOrganiserProfile(organizerId);
            const profile = normalizeOrganizerProfile(response?.data ?? response);
            dispatch(setEditedOrganizer(profile || organizer));
        } catch (error) {
            console.error('Blad pobierania danych organizatora do edycji:', error);
            dispatch(setEditedOrganizer(organizer));
        }

        dispatch(toggleEditModal());
    };

    const showInProgressMessage = () => {
        alert('W trakcie implementacji.');
    };

    return organizers.map((org) => {
        const isChecked = checkedItems.includes(org.id);
        const requiresReview = hasOrganizerPendingDecision(org);
        const organizerStatuses = getOrganizerStatuses(org);
        const isNewProfileReview = organizerStatuses.includes(ORGANIZER_STATUS_KEYS.NEW_PROFILE_PENDING);
        const isDataChangeReview = !isNewProfileReview
            && organizerStatuses.includes(ORGANIZER_STATUS_KEYS.DATA_CHANGE_PENDING);

        const actionItems = [
            {
                key: 'details',
                label: 'Szczegoly',
                icon: StatusUp,
                onClick: () => openOrganizerDetails(org),
            },
            {
                key: 'edit',
                label: 'Edytuj',
                icon: Edit,
                onClick: () => openOrganizerEdit(org),
            },
        ];

        if (requiresReview) {
            actionItems.push(
                {
                    key: 'approve',
                    label: isDataChangeReview ? 'Akceptuj zmiany' : 'Zaakceptuj',
                    icon: TickCircle,
                    onClick: () => openOrganizerDetails(org),
                },
                {
                    key: 'reject',
                    label: isDataChangeReview ? 'Odrzuc zmiany' : 'Odrzuc',
                    icon: CloseCircle,
                    danger: true,
                    onClick: () => openOrganizerDetails(org),
                }
            );
        } else {
            actionItems.push({
                key: 'delete',
                label: 'Usun',
                icon: Trash,
                danger: true,
                onClick: showInProgressMessage,
            });
        }

        const commonColumns = [
            <label
                key={`checkbox-${org.id}`}
                className="checkbox-wrapper"
                onClick={(e) => e.stopPropagation()}
            >
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => dispatch(toggleCheckedItem(org.id))}
                />
            </label>,
            <ActionMenu key={`actions-${org.id}`} items={actionItems} />,
            formatDate(org.createdAt || org.registrationDate),
            <span
                key={`company-${org.id}`}
                className="company-name-link"
                onClick={() => dispatch(setSelectedOrganizer(org))}
            >
                {org.companyName}
            </span>,
            org.nip,
            <span key={`address-${org.id}`} className="address-cell">
                {org.address}
            </span>,
            <span key={`contact-${org.id}`} className="contact-cell">
                {org.email}
            </span>,
            org.eventsCount ?? 0,
        ];

        return [
            ...commonColumns,
            <div key={`status-${org.id}`} className="organizer-status-list">
                {getOrganizerStatuses(org).map((statusKey) => {
                    const statusMeta = ORGANIZER_STATUS_META[statusKey] || ORGANIZER_STATUS_META[ORGANIZER_STATUS_KEYS.ACTIVE];
                    return (
                        <span key={statusKey} className={`organizer-status ${statusMeta.toneClass}`}>
                            {statusMeta.label}
                        </span>
                    );
                })}
            </div>,
        ];
    });
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).replace(/\//g, '-');
};

export const resolveOrganizerToneClass = (statusKey) => {
    const normalized = String(statusKey || 'ALL').toUpperCase();
    if (normalized === 'ALL') return 'organizer-status--all';
    if (normalized === 'PENDING') return 'organizer-status--pending';
    if (normalized === 'ACTIVE') return 'organizer-status--active';
    return 'organizer-status--inactive';
};

const resolveOrganizerFilterQuery = (filterQuery) => {
    const normalized = String(filterQuery || 'ALL').toUpperCase();
    if (['ALL', 'ACTIVE', 'NOTCONFIRMED', 'PENDING'].includes(normalized)) {
        return normalized;
    }
    return 'ALL';
};
